/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  CreatePaymentBody,
  PaymentsByBillResponse,
  PaymentsByCustomerResponse,
  PaymentDetailResponse,
  PaymentsSummary,
} from '@/types/backend'

export const usePaymentsByBillQuery = (
  billId?: string,
  enabled: boolean = !!billId
) =>
  useQuery({
    queryKey: ['payments', 'by-bill', billId],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentsByBillResponse>(
        `/payments/by-bill/${billId}`
      )
      return data
    },
  })

export const usePaymentsByCustomerQuery = (
  customerId?: string,
  enabled: boolean = !!customerId
) =>
  useQuery({
    queryKey: ['payments', 'by-customer', customerId],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentsByCustomerResponse>(
        `/payments/by-customer/${customerId}`
      )
      return data
    },
  })

export const usePaymentQuery = (id?: string, enabled: boolean = !!id) =>
  useQuery({
    queryKey: ['payments', 'one', id],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentDetailResponse>(
        `/payments/${id}`
      )
      return data
    },
  })

export const usePaymentsSummaryQuery = (from?: string, to?: string) =>
  useQuery({
    queryKey: ['payments', 'summary', { from, to }],
    queryFn: async () => {
      const { data } = await apiClient.get<PaymentsSummary>(
        `/payments/_summary`,
        {
          params: { from, to },
        }
      )
      return data
    },
  })

export const useCreatePaymentMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePaymentBody) => {
      const { data } = await apiClient.post<unknown>('/payments', payload)
      return data
    },
    onSuccess: (_data, vars) => {
      const v = vars as Partial<CreatePaymentBody>
      if (v.billId) {
        qc.invalidateQueries({ queryKey: ['payments', 'by-bill', v.billId] })
        qc.invalidateQueries({ queryKey: ['bills', 'detail', v.billId] })
      }
      if (v.customerId) {
        qc.invalidateQueries({
          queryKey: ['payments', 'by-customer', v.customerId],
        })
      }
      qc.invalidateQueries({ queryKey: ['payments', 'summary'] })
      qc.invalidateQueries({ queryKey: ['bills'] })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export const useDeletePaymentMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      id: string
      billId?: string
      customerId?: string
    }) => {
      const { id } = args
      const { data } = await apiClient.delete<unknown>(`/payments/${id}`)
      return data
    },
    onSuccess: (_data, vars) => {
      const anyVars = vars as any
      if (anyVars?.billId)
        qc.invalidateQueries({
          queryKey: ['payments', 'by-bill', anyVars.billId],
        })
      if (anyVars?.customerId)
        qc.invalidateQueries({
          queryKey: ['payments', 'by-customer', anyVars.customerId],
        })
      qc.invalidateQueries({ queryKey: ['payments', 'summary'] })
      qc.invalidateQueries({ queryKey: ['bills'] })
    },
  })
}
